import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useBranch } from "@/contexts/BranchContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { fmtIDR, fmtShortIDR, fmtDateTime, firstOfMonthISO, todayISO } from "@/lib/format";
import { toast } from "sonner";
import {
  Wallet, Download, Settings, FileSpreadsheet, Stethoscope, HandHeart,
} from "lucide-react";
import StatCard from "@/components/StatCard";

export default function Commissions() {
  const { user } = useAuth();
  const { branchId } = useBranch();
  const [dateFrom, setDateFrom] = useState(firstOfMonthISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [summary, setSummary] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [configForm, setConfigForm] = useState({ staff_id: "", staff_type: "", commission_rate_pct: "", commission_per_treatment: "" });

  const isAdmin = user?.role === "owner" || user?.role === "manager";
  const isSelf = user?.role === "doctor" || user?.role === "therapist";

  const fetchSummary = () => {
    api.get("/commissions/summary", { params: { date_from: dateFrom, date_to: dateTo, branch_id: branchId } })
      .then((r) => setSummary(r.data));
  };

  useEffect(() => { fetchSummary(); }, [branchId, dateFrom, dateTo]);

  const openDetail = async (row) => {
    const { data } = await api.get("/commissions/detail", {
      params: {
        staff_id: row?.staff_id,
        staff_type: row?.staff_type,
        date_from: dateFrom, date_to: dateTo,
      },
    });
    setDetail(data);
    setDetailOpen(true);
  };

  const downloadExcel = async (row) => {
    const params = new URLSearchParams();
    if (row) {
      params.set("staff_id", row.staff_id);
      params.set("staff_type", row.staff_type);
    }
    params.set("date_from", dateFrom);
    params.set("date_to", dateTo);
    const token = localStorage.getItem("myclinic_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/commissions/export.xlsx?${params.toString()}`;
    try {
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Download gagal");
      const blob = await resp.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `komisi_${row?.name || "all"}_${dateFrom}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Excel terunduh");
    } catch (e) {
      toast.error("Gagal mengunduh");
    }
  };

  const openConfig = (row) => {
    setConfigForm({
      staff_id: row.staff_id,
      staff_type: row.staff_type,
      commission_rate_pct: row.commission_rate_pct || "",
      commission_per_treatment: row.commission_per_treatment || "",
    });
    setConfigOpen(true);
  };

  const saveConfig = async (e) => {
    e?.preventDefault?.();
    const payload = { staff_type: configForm.staff_type };
    if (configForm.staff_type === "doctor") payload.commission_rate_pct = Number(configForm.commission_rate_pct);
    else payload.commission_per_treatment = Number(configForm.commission_per_treatment);
    try {
      await api.patch(`/commissions/config/${configForm.staff_id}`, payload);
      toast.success("Rate komisi diperbarui");
      setConfigOpen(false);
      fetchSummary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal");
    }
  };

  const rows = summary?.rows || [];
  const doctorRows = rows.filter((r) => r.staff_type === "doctor");
  const therapistRows = rows.filter((r) => r.staff_type === "therapist");

  return (
    <div className="space-y-6" data-testid="commissions-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">
            Komisi {isSelf && <Badge className="ml-2 align-middle">Komisi Saya</Badge>}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isAdmin
              ? "Rekap komisi semua dokter & therapist untuk payroll dan audit."
              : "Anda hanya melihat komisi pribadi. Download Excel untuk arsip."}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-xs text-muted-foreground">Dari</label>
            <Input type="date" data-testid="comm-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[160px]" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Sampai</label>
            <Input type="date" data-testid="comm-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[160px]" />
          </div>
          {isAdmin && (
            <Button data-testid="download-all-btn" onClick={() => downloadExcel(rows[0])} variant="outline">
              <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Export Excel
            </Button>
          )}
        </div>
      </div>

      {summary?.unlinked && (
        <div data-testid="comm-unlinked-notice" className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Akun Anda belum terhubung ke profil {user?.role === "doctor" ? "dokter" : "therapist"}. Komisi pribadi belum dapat ditampilkan — hubungi Owner untuk menautkan akun Anda ke profil staf.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard testId="comm-doctor-total" label="Total Komisi Dokter" value={summary ? fmtShortIDR(summary.total_doctor_commission) : "—"} icon={Stethoscope} accent="indigo" />
        <StatCard testId="comm-therapist-total" label="Total Komisi Therapist" value={summary ? fmtShortIDR(summary.total_therapist_commission) : "—"} icon={HandHeart} accent="emerald" />
        <StatCard testId="comm-grand-total" label="Grand Total" value={summary ? fmtShortIDR((summary.total_doctor_commission || 0) + (summary.total_therapist_commission || 0)) : "—"} icon={Wallet} accent="amber" />
        <StatCard testId="comm-staff-count" label="Staff Aktif" value={rows.length} sublabel={`${doctorRows.length} dokter, ${therapistRows.length} therapist`} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Stethoscope className="h-4 w-4" /> Dokter ({doctorRows.length})
          </CardTitle>
          <CardDescription>Komisi = % dari amount transaksi. Rate per dokter configurable.</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dokter</TableHead>
                <TableHead>Rate</TableHead>
                <TableHead className="text-right">Tx</TableHead>
                <TableHead className="text-right">Gross Revenue</TableHead>
                <TableHead className="text-right">Komisi</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {doctorRows.map((r) => (
                <TableRow key={r.staff_id} data-testid={`doc-comm-${r.staff_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      {r.avatar_url ? <img src={r.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover" /> : <div className="h-8 w-8 rounded-full bg-secondary" />}
                      <div>
                        <div className="text-sm font-medium">{r.name}</div>
                        <div className="text-xs text-muted-foreground">{r.specialty}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{r.commission_rate_pct}%</Badge></TableCell>
                  <TableCell className="text-right text-sm">{r.tx_count}</TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">{fmtIDR(r.gross_revenue)}</TableCell>
                  <TableCell className="text-right text-sm font-medium">{fmtIDR(r.commission_total)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" data-testid={`detail-${r.staff_id}`} onClick={() => openDetail(r)}>Detail</Button>
                      <Button size="sm" variant="outline" data-testid={`download-${r.staff_id}`} onClick={() => downloadExcel(r)}>
                        <Download className="h-3 w-3" />
                      </Button>
                      {isAdmin && (
                        <Button size="sm" variant="ghost" data-testid={`config-${r.staff_id}`} onClick={() => openConfig(r)}>
                          <Settings className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {doctorRows.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-6">Belum ada komisi dokter.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <HandHeart className="h-4 w-4" /> Therapist ({therapistRows.length})
          </CardTitle>
          <CardDescription>Komisi = Rp tetap per treatment finished. Configurable per therapist.</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Therapist</TableHead>
                <TableHead>Per Treatment</TableHead>
                <TableHead className="text-right">Treatment</TableHead>
                <TableHead className="text-right">Komisi</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {therapistRows.map((r) => (
                <TableRow key={r.staff_id} data-testid={`ther-comm-${r.staff_id}`}>
                  <TableCell className="text-sm font-medium">{r.name}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{fmtIDR(r.commission_per_treatment)}</Badge></TableCell>
                  <TableCell className="text-right text-sm">{r.tx_count}</TableCell>
                  <TableCell className="text-right text-sm font-medium">{fmtIDR(r.commission_total)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => openDetail(r)}>Detail</Button>
                      <Button size="sm" variant="outline" data-testid={`download-th-${r.staff_id}`} onClick={() => downloadExcel(r)}>
                        <Download className="h-3 w-3" />
                      </Button>
                      {isAdmin && (
                        <Button size="sm" variant="ghost" onClick={() => openConfig(r)}><Settings className="h-3 w-3" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {therapistRows.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">Belum ada komisi therapist.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="comm-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Detail Komisi — {detail?.name}</DialogTitle>
            <DialogDescription>
              {detail?.staff_type === "doctor" ? `Rate: ${detail?.rate_pct}%` : `Per treatment: ${fmtIDR(detail?.per_treatment)}`}
              {" • "}{dateFrom} → {dateTo}
            </DialogDescription>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tanggal</TableHead>
                <TableHead>Pasien</TableHead>
                <TableHead>Treatment</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Komisi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(detail?.rows || []).map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs">{fmtDateTime(r.paid_at)}</TableCell>
                  <TableCell className="text-sm">{r.patient_name}</TableCell>
                  <TableCell className="text-xs">{r.treatment_name}</TableCell>
                  <TableCell className="text-right text-xs">{fmtIDR(r.amount)}</TableCell>
                  <TableCell className="text-right text-sm font-medium">{fmtIDR(r.commission)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent data-testid="comm-config-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Konfigurasi Komisi</DialogTitle>
            <DialogDescription>Hanya owner/manager yang bisa mengubah rate.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveConfig} className="space-y-4">
            {configForm.staff_type === "doctor" ? (
              <div>
                <Label>Commission Rate (%)</Label>
                <Input type="number" min="0" max="100" step="any" data-testid="comm-rate-input" value={configForm.commission_rate_pct} onChange={(e) => setConfigForm({ ...configForm, commission_rate_pct: e.target.value })} required />
              </div>
            ) : (
              <div>
                <Label>Per Treatment (Rp)</Label>
                <Input type="number" min="0" step="1" data-testid="comm-per-tx-input" value={configForm.commission_per_treatment} onChange={(e) => setConfigForm({ ...configForm, commission_per_treatment: e.target.value })} required />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setConfigOpen(false)}>Batal</Button>
              <Button type="submit" data-testid="comm-config-save">Simpan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
